import { EMPTY_VALUE_PLACEHOLDER } from '@sensika/ag-grid-kit';
import { TranslateService } from '@ngx-translate/core';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { selectUser } from 'src/app/core/reducers';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import {
  ImageFallbacks,
  NomObjectClass,
  NomObjectLifecycleStatus,
} from 'src/app/core/constants';
import {
  getIconForDataObject,
  getNestedProperty,
} from 'src/app/shared/utils/object-utils';

import { ICellRendererAngularComp } from 'ag-grid-angular';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ObjectCardDropdownComponent } from 'src/app/core/components/cards/object-card/object-card-dropdown/object-card-dropdown.component';
import { hasObjectCardMenuItems } from 'src/app/core/components/cards/object-card/object-card-dropdown/object-card-menu-availability';
import { take, tap } from 'rxjs';
import { editorSaved } from 'src/app/shared/utils/editor-close.utils';

@Component({
  selector: 'app-ag-data-object-cell-renderer',
  templateUrl: './ag-data-object-cell-renderer.component.html',
  styleUrl: './ag-data-object-cell-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgDataObjectCellRendererComponent implements ICellRendererAngularComp {
  @ViewChild('cardDropdownContainer', { read: ViewContainerRef })
  container: ViewContainerRef;
  params: any;
  title: string;
  icon: IconProp;
  avatarUrl: string;
  objectId: string;
  objectClassId: number;
  objectTypeId: number;
  lifecycleStatus: NomObjectLifecycleStatus;
  disableMenu: boolean = false;
  tooltip: string;
  protected readonly placeholder = EMPTY_VALUE_PLACEHOLDER;
  private readonly translate = inject(TranslateService);
  /** Hint on the placeholder: which filter isolates the empty rows. */
  protected get emptyTooltip(): string {
    return this.translate.instant('grid.tooltip.no-value-filter-blank');
  }
  hasMenu: boolean;
  shouldInvite: boolean = false;
  /** The nested object named by `cellRendererParams.object`, if any - what onClick receives. */
  private clickTarget: unknown = null;

  NomObjectClass = NomObjectClass;
  readonly avatarFallback = ImageFallbacks.avatar;

  // Variables for dropdown toggle tracking
  dropdownVisible = false;
  private dropdownRef: ComponentRef<ObjectCardDropdownComponent> | null = null;

  private readonly store = inject(Store);
  /** The signed-in user's USER_ACCESS_LEVEL, read once per renderer instance. */
  private userAccessLevel: UserAccessLevel = UserAccessLevel.CLIENT_USER;

  constructor(private cdr: ChangeDetectorRef) {
    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe((user) => {
        this.userAccessLevel = (user?.user_access_level?.id ??
          UserAccessLevel.CLIENT_USER) as UserAccessLevel;
      });
  }

  agInit(params: any): void {
    this.setParams(params);
  }

  refresh(params: any): boolean {
    this.setParams(params);
    this.cdr.detectChanges(); // or markForCheck()
    return true;
  }

  setParams(params: any): void {
    this.params = params;
    const objectData = params.data;
    this.tooltip = params?.tooltip ?? '';
    this.shouldInvite = params?.shouldInvite ?? false;

    const rendererParams = params.colDef?.cellRendererParams ?? {};

    // `object` names the nested object this cell renders (e.g. 'owner', 'user',
    // 'created_by_user'). With it set, every *Key param is RELATIVE to that
    // object and defaults to the canonical DataObject field names, and onClick
    // receives the resolved object itself - so a column can never render one
    // key and click another. Without it, keys are absolute row paths (legacy).
    const objectPath = rendererParams.object as string | undefined;
    this.clickTarget = objectPath
      ? getNestedProperty(objectData, objectPath, null)
      : null;
    const source = objectPath ? this.clickTarget : objectData;

    const titleKey =
      rendererParams.titleKey ?? (objectPath ? 'name' : undefined);
    const avatarKey =
      rendererParams.avatarKey ?? (objectPath ? 'avatar_url' : undefined);
    const idKey = rendererParams.objectId ?? (objectPath ? 'id' : undefined);
    const classIdKey =
      rendererParams.objectClassId ??
      (objectPath ? 'object_class_id' : undefined);
    const typeIdKey =
      rendererParams.objectTypeId ??
      (objectPath ? 'object_type_id' : undefined);
    const lifecycleStatusKey =
      rendererParams.lifecycleStatus ??
      (objectPath ? 'lifecycle_status' : undefined);
    this.disableMenu = rendererParams.disableMenu;

    this.title = getNestedProperty(source, titleKey, null);
    this.avatarUrl = getNestedProperty(source, avatarKey, null);
    this.objectId = getNestedProperty(source, idKey, null);
    // `objectClass` is a static NomObjectClass VALUE for payloads that do not
    // carry the class id field (e.g. the rules grid's owner).
    this.objectClassId =
      rendererParams.objectClass ?? getNestedProperty(source, classIdKey, null);
    this.objectTypeId = getNestedProperty(source, typeIdKey, null);
    this.lifecycleStatus = getNestedProperty(source, lifecycleStatusKey, null);

    if (this.objectClassId !== NomObjectClass.USER) {
      this.icon =
        params.icon ??
        getIconForDataObject(this.objectClassId, this.objectTypeId);
    }

    if (!this.disableMenu) this.estimateHasMenu();
  }

  /**
   * A title is a link when it has a click handler AND the user may open what it
   * leads to: `cellRendererParams.clickAccessLevel` (USER_ACCESS_LEVEL, the same
   * threshold as the dropdown's Settings entry) and the target's
   * OBJECT_PERMISSIONS `can_view` flag - read off the row when the title names
   * the row's own object, off the resolved nested object when `object` is set
   * (user/company refs carry no `can_*` flags, so they gate on the access level
   * alone; refs that do carry them, like a workspace, are honored).
   */
  get isClickable(): boolean {
    if (!this.params?.onClick) {
      return false;
    }
    const required = this.params.colDef?.cellRendererParams
      ?.clickAccessLevel as UserAccessLevel | undefined;
    if (required != null && this.userAccessLevel < required) {
      return false;
    }
    if (this.params.colDef?.cellRendererParams?.object) {
      // A missing user/company still arrives as a ref object (the canonical
      // "N/A" shape with id: null, or an empty owner include) - without a
      // resolved id there is nothing to open, so the title is not a link.
      const target = this.clickTarget as { can_view?: boolean } | null;
      if (
        target == null ||
        this.objectId == null ||
        target.can_view === false
      ) {
        return false;
      }
      // Optional per-column gate for refs that resolve but have no UI target
      // (e.g. the activity log lists object classes without a viewer).
      const clickable = this.params.colDef?.cellRendererParams?.clickable as
        | ((target: unknown, row: unknown) => boolean)
        | undefined;
      return !clickable || clickable(target, this.params.data) !== false;
    }
    return !this.titleIsRowObject() || this.params.data?.can_view !== false;
  }

  /** The title names the row itself unless `object` or a nested `objectId` points elsewhere. */
  private titleIsRowObject(): boolean {
    if (this.params?.colDef?.cellRendererParams?.object) {
      return false;
    }
    const idKey = this.params?.colDef?.cellRendererParams?.objectId;
    return !idKey || !String(idKey).includes('.');
  }

  /**
   * With `object` set the handler receives the resolved nested object first
   * and the row second; otherwise the row alone, as before.
   */
  onClick(): void {
    if (!this.isClickable) {
      return;
    }
    const close = this.params.onClick(
      this.clickTarget ?? this.params.data,
      this.params.data,
    );
    // * an editor handle (EditorClose) resolves once the dialog closed - a saved
    // * edit is back-populated by re-fetching the loaded rows
    editorSaved(close).then((saved) => {
      if (saved) {
        this.refreshHostRows();
      }
    });
  }

  /** Re-fetches the host grid's loaded rows in place; a no-op off the server-side row model. */
  private refreshHostRows(): void {
    const api = this.params?.api;
    if (
      api &&
      !api.isDestroyed() &&
      api.getGridOption('rowModelType') === 'serverSide'
    ) {
      api.refreshServerSide({ purge: false });
    }
  }

  estimateHasMenu() {
    // The hamburger shows only when opening the object-card dropdown would
    // yield at least one action for this object and viewer - the shared
    // availability metadata answers that (a Settings-only menu like
    // COMPANY's never shows: the title link already opens Settings).
    const row = this.params?.data;
    const stripRequiresEdit =
      row != null && row.id === this.objectId && row.can_edit === false;

    this.hasMenu = hasObjectCardMenuItems(
      this.objectClassId,
      this.lifecycleStatus,
      this.userAccessLevel,
      stripRequiresEdit,
    );
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();

    if (this.dropdownVisible) {
      this.closeDropdown();
      return;
    }

    this.openDropdown();
  }

  private openDropdown(): void {
    this.container.clear(); // Optional, in case you want a fresh render
    this.dropdownRef = this.container.createComponent(
      ObjectCardDropdownComponent,
    );
    this.dropdownRef.instance.objectId = this.objectId;
    this.dropdownRef.instance.objectClassId = this.objectClassId;
    this.dropdownRef.instance.objectTypeId = this.objectTypeId;
    this.dropdownRef.instance.lifecycleStatus = this.lifecycleStatus;
    this.dropdownRef.instance.objectTitle = this.title;
    this.dropdownRef.instance.shouldInvite = this.shouldInvite;
    this.dropdownRef.instance.rowData = this.params;
    this.dropdownRef.instance.rowDataChange.subscribe((rowData) =>
      this.params.node.setData(rowData.data),
    );
    this.dropdownRef.instance.saved.subscribe(() => this.refreshHostRows());
    // manually trigger lifecycle hook if needed (usually not needed)
    this.dropdownRef.instance.ngOnInit?.();
    this.dropdownVisible = true;
    this.dropdownRef.instance.dropdownClosed
      .pipe(
        tap(() => {
          this.closeDropdown();
        }),
      )
      .subscribe();
  }

  private closeDropdown(): void {
    this.container.clear();
    this.dropdownRef?.destroy();
    this.dropdownRef = null;
    this.dropdownVisible = false;
  }
}
